import { useState } from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, CheckCircle, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { ReferrerVerificationStep } from '@/components/referrals/ReferrerVerificationStep';
import { AddReferralsStep } from '@/components/referrals/AddReferralsStep';
import { ReviewSubmitStep } from '@/components/referrals/ReviewSubmitStep';
import { ReferralConfirmation } from '@/components/referrals/ReferralConfirmation';

const ReferUs = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Referrer info
    referrerFirstName: '',
    referrerLastName: '',
    referrerEmail: '',
    referrerPhone: '',
    referrerVerified: false,
    verificationToken: '',
    
    // Referrals array (1-5)
    referrals: []
  });
  const [submitted, setSubmitted] = useState(false);
  const [submissionId, setSubmissionId] = useState('');

  const totalSteps = 3;
  const progress = (currentStep / totalSteps) * 100;

  const updateFormData = (data) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async (data) => {
    setSubmitted(true);
    setSubmissionId(data.submission_id);
  };

  if (submitted) {
    return <ReferralConfirmation formData={formData} submissionId={submissionId} />;
  }

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Helmet>
        <title>Refer Someone to KGOB | KGOB CPAs</title>
        <meta name="description" content="Know someone who needs a trusted tax and accounting partner? Refer them to KGOB CPAs & Advisors." />
        <link rel="canonical" href="https://kgob.com/refer-us" />
      </Helmet>
      
      <Header />
      
      <main className="py-12 lg:py-20">
        {/* Hero Section */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl mb-12">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#D4AF37]/10 mb-6">
              <Users className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <h1 className="font-['Playfair_Display'] text-4xl font-bold text-[#0B2240] mb-4">
              Know Someone Who Needs a Trusted Tax & Accounting Partner?
            </h1>
            <p className="text-lg text-[#4C5A70] max-w-3xl mx-auto">
              If you have a friend, family member, or fellow business owner who might benefit from working with a firm like ours, we'd be honored to be introduced. There's never any pressure—our goal is simply to be a helpful resource.
            </p>
          </motion.div>
        </div>

        {/* Form Section */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between mb-2">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex flex-col items-center flex-1">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                      currentStep === step
                        ? 'bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]'
                        : currentStep > step
                        ? 'bg-[#D4AF37] text-white'
                        : 'bg-[#E1DFD7] text-[#4C5A70]'
                    }`}
                  >
                    {currentStep > step ? <CheckCircle className="w-6 h-6" /> : step}
                  </div>
                  <span className="text-xs text-[#4C5A70] mt-2">
                    {step === 1 ? 'Your Info' : step === 2 ? 'Add Referrals' : 'Review'}
                  </span>
                </div>
              ))}
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Current Step */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="p-8 border-2 border-[#E1DFD7]">
                {currentStep === 1 && (
                  <ReferrerVerificationStep
                    formData={formData}
                    updateFormData={updateFormData}
                    onNext={handleNext}
                  />
                )}
                {currentStep === 2 && (
                  <AddReferralsStep
                    formData={formData}
                    updateFormData={updateFormData}
                    onNext={handleNext}
                    onBack={handleBack}
                  />
                )}
                {currentStep === 3 && (
                  <ReviewSubmitStep data-testid="refer-us-reviewsubmitstep-1"
                    formData={formData}
                    onBack={handleBack}
                    onSubmit={handleSubmit}
                  />
                )}
              </Card>
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ReferUs;